import { getAuthToken } from './auth/tokenStorage';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('Spouse');
export interface Spouse {
  id: string;
  taskId: number;
  firstName?: string;
  lastName?: string;
  maidenName?: string;
  dateOfBirth?: Date;
  phoneNumber?: string;
  email?: string;
  occupation?: string;
  employer?: string;
  workPhone?: string;
  workEmail?: string;
  workAddress?: string;
  ssn?: string;
  driversLicense?: string;
  passportNumber?: string;
  marriageDate?: Date;
  marriageLocation?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
  medicalConditions?: string;
  allergies?: string;
  medications?: string;
  additionalNotes?: string;
  isLocked: boolean;
}

export interface CreateSpouseData {
  taskId: number;
  firstName?: string;
  lastName?: string;
  maidenName?: string;
  dateOfBirth?: Date;
  phoneNumber?: string;
  email?: string;
  occupation?: string;
  employer?: string;
  workPhone?: string;
  workEmail?: string;
  workAddress?: string;
  ssn?: string;
  driversLicense?: string;
  passportNumber?: string;
  marriageDate?: Date;
  marriageLocation?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
  medicalConditions?: string;
  allergies?: string;
  medications?: string;
  additionalNotes?: string;
}

export interface UpdateSpouseData extends CreateSpouseData {
  isLocked?: boolean;
}

class SpouseService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async getSpouse(taskId: number): Promise<Spouse[]> {
    const response = await this.makeRequest(`/api/spouse-info?taskId=${taskId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch spouse info: ${response.status}`);
    }
    
    return await response.json();
  }

  async createSpouse(data: CreateSpouseData): Promise<{ id: string }> {
    const response = await this.makeRequest('/api/spouse-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create spouse info: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: {...} }
    if (result && result.success && result.data) {
      return result.data;
    }

    // Fallback for direct response
    return result;
  }

  async updateSpouse(id: string, data: UpdateSpouseData): Promise<Spouse> {
    const response = await this.makeRequest(`/api/spouse-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update spouse info: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: {...} }
    if (result && result.success && result.data) {
      return result.data;
    }

    // Fallback for direct response
    return result;
  }

  async deleteSpouse(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/spouse-info/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete spouse info: ${response.status}`);
    }
  }

  async getAllSpouse(targetUserId?: string): Promise<Spouse[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await this.makeRequest(`/api/spouse-info${params}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch all spouse info: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: [...] }
    if (result && result.success && Array.isArray(result.data)) {
      return result.data;
    }

    // Fallback for direct array response
    return Array.isArray(result) ? result : [];
  }

  async getSpouseInfo(targetUserId?: string): Promise<Spouse[]> {
    return this.getAllSpouse(targetUserId);
  }

  async setLockState(id: string, isLocked: boolean): Promise<Spouse> {
    const response = await this.makeRequest(`/api/spouse-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isLocked }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update lock state: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, data: {...} }
    if (result && result.success && result.data) {
      return result.data;
    }

    // Fallback for direct response
    return result;
  }

  async markAsCompleted(taskId: number): Promise<boolean> {
    const response = await this.makeRequest(`/api/spouse-info/complete/${taskId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to mark spouse info as completed: ${response.status}`);
    }

    return await response.json();
  }

  async lockSpouseInfo(spouseId: string): Promise<void> {
    const response = await this.makeRequest(`/api/spouse-info/${spouseId}/lock`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to lock spouse info: ${response.status}`);
    }
  }

  async unlockSpouseInfo(spouseId: string, emails?: string[]): Promise<void> {
    const response = await this.makeRequest(`/api/spouse-info/${spouseId}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ emails: emails || [] }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to unlock spouse info: ${response.status}`);
    }
  }


  // Vault integration methods for sensitive data. The old "local fallback"
  // stashed SSN / driver's license / passport number as plaintext JSON in
  // AsyncStorage whenever the backend returned 404 or NETWORK_ERROR — removed
  // in the Step 4 keychain migration. Callers must now handle the failure;
  // see docs/KNOWN_LIMITATIONS.md.
  async storeSpouseCredentials(spouseId: string, data: { ssn: string; driversLicense: string; passportNumber: string }): Promise<void> {
    const response = await this.makeRequest(`/api/vault/spouse-credentials/${spouseId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to store spouse credentials: ${response.status}`);
    }
  }

  async getSpouseCredentials(spouseId: string): Promise<{ ssn: string; driversLicense: string; passportNumber: string }> {
    const response = await this.makeRequest(`/api/vault/spouse-credentials/${spouseId}`);
    if (!response.ok) {
      if (response.status === 404) {
        // No credentials stored yet — return empty shape rather than throw.
        return { ssn: '', driversLicense: '', passportNumber: '' };
      }
      throw new Error(`Failed to fetch spouse credentials: ${response.status}`);
    }
    return await response.json();
  }

  async deleteSpouseCredentials(spouseId: string): Promise<void> {
    const response = await this.makeRequest(`/api/vault/spouse-credentials/${spouseId}`, {
      method: 'DELETE',
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete spouse credentials: ${response.status}`);
    }
  }

}

export const spouseService = new SpouseService();